import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { VehicleCapacity } from '../../fleet/vehicle/params/vehicle-capacity';
import { VehicleConfig } from '../../fleet/vehicle/params/vehicle-config';
import { VehicleType } from '../../fleet/vehicle/params/vehicle-type';
import { City } from '../../models/common/city.model';
import { Country } from '../../models/common/country.model';
import { BaseMasterDataService } from '../../services/base-master-data.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import {
  InternalTransportCostCard,
  InternalTransportCostImportBatch,
  InternalTransportCostImportPreview,
  InternalTransportCostImportRow,
  InternalTransportCostImportStatus
} from './internal-transport-costs.model';
import { InternalTransportCostsService } from './internal-transport-costs.service';

@Component({
  selector: 'app-internal-transport-costs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ToastModule,
    BreadcrumbModule,
    ButtonModule,
    DatePickerModule,
    DialogModule,
    IftaLabelModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputNumberModule,
    InputTextModule,
    TextareaModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  providers: [BaseMasterDataService, MessageService],
  templateUrl: './internal-transport-costs.component.html',
  styleUrls: ['./internal-transport-costs.component.css']
})
export class InternalTransportCostsComponent implements OnInit {
  readonly importColumns = [
    'sourceCountry',
    'sourceCity',
    'destinationCountry',
    'destinationCity',
    'vehicleType',
    'vehicleConfig',
    'vehicleCapacity',
    'baseCostAmount',
    'currencyCode',
    'waitingThresholdMinutes',
    'waitingChargeAmount',
    'effectiveStartDate',
    'effectiveEndDate',
    'status',
    'notes'
  ];

  breadcrumbItems: MenuItem[] = [{ label: 'Shipments', routerLink: '/shipment/company' }, { label: 'Internal Transport Costs' }];
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

  countries: Country[] = [];
  vehicleTypes: VehicleType[] = [];
  vehicleConfigs: VehicleConfig[] = [];
  vehicleCapacities: VehicleCapacity[] = [];
  private readonly cityOptionsByCountry = new Map<number, City[]>();
  private readonly cityDirectory = new Map<number, City>();

  cards: InternalTransportCostCard[] = [];
  loading = false;
  saving = false;
  processing = false;
  totalRecords = 0;
  first = 0;
  page = 0;
  pageSize = 50;
  readonly rowsPerPageOptions = [25, 50, 100, 200];

  filterSourceCountryId?: number;
  filterSourceCityId?: number;
  filterDestinationCountryId?: number;
  filterDestinationCityId?: number;
  filterVehicleTypeId?: number;
  filterVehicleConfigId?: number;
  filterVehicleCapacityId?: number;
  filterStatus?: 'ACTIVE' | 'INACTIVE';
  filterEffectiveOn?: Date | null;
  keyword = '';

  formVisible = false;
  costForm: InternalTransportCostCard = this.newCard();
  formVehicleConfigs: VehicleConfig[] = [];
  formVehicleCapacities: VehicleCapacity[] = [];
  formEffectiveStartDate?: Date | null;
  formEffectiveEndDate?: Date | null;

  importFile?: File;
  currentImport?: InternalTransportCostImportPreview;
  importBatches: InternalTransportCostImportBatch[] = [];
  editRowDialogVisible = false;
  editingRow?: InternalTransportCostImportRow;
  editingValues: Record<string, string> = {};

  statusOptions = [
    { label: 'All statuses', value: undefined },
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Inactive', value: 'INACTIVE' }
  ];

  constructor(
    private readonly masterDataService: BaseMasterDataService,
    private readonly session: SessionStorageService,
    private readonly permissions: PermissionService,
    private readonly messageService: MessageService,
    private readonly service: InternalTransportCostsService
  ) {}

  ngOnInit(): void {
    this.countries = this.session.getCountryList() ?? [];
    this.vehicleTypes = this.session.getVehicleTypes() ?? [];
    this.vehicleConfigs = this.vehicleTypes.flatMap(type => type.vehicleConfigs || []);
    this.vehicleCapacities = this.vehicleTypes.flatMap(type => type.vehicleCapacities || []);
    this.loadCards();
    this.loadBatches();
  }

  get canCreate(): boolean {
    return this.permissions.hasPermission(app_permissions.shipments.internal_transport_costs.create);
  }

  get canEdit(): boolean {
    return this.permissions.hasPermission(app_permissions.shipments.internal_transport_costs.edit);
  }

  get canImport(): boolean {
    return this.permissions.hasPermission(app_permissions.shipments.internal_transport_costs.import);
  }

  get filterSourceCities(): City[] {
    return this.citiesForCountry(this.filterSourceCountryId);
  }

  get filterDestinationCities(): City[] {
    return this.citiesForCountry(this.filterDestinationCountryId);
  }

  get filterVehicleConfigs(): VehicleConfig[] {
    if (!this.filterVehicleTypeId) {
      return [];
    }
    return this.vehicleConfigs.filter(config => config.typeId === this.filterVehicleTypeId);
  }

  get filterVehicleCapacities(): VehicleCapacity[] {
    if (!this.filterVehicleTypeId) {
      return [];
    }
    return this.vehicleCapacities.filter(capacity => capacity.typeId === this.filterVehicleTypeId);
  }

  get formSourceCities(): City[] {
    return this.citiesForCountry(this.costForm.sourceCountryId);
  }

  get formDestinationCities(): City[] {
    return this.citiesForCountry(this.costForm.destinationCountryId);
  }

  get selectableStatusOptions(): { label: string; value: 'ACTIVE' | 'INACTIVE' | undefined }[] {
    return this.statusOptions.filter(option => option.value !== undefined) as { label: string; value: 'ACTIVE' | 'INACTIVE' }[];
  }

  metrics(): PageMetric[] {
    const rows = this.currentImport?.rows || [];
    return [
      { label: 'Cost Cards', value: this.totalRecords, hint: 'Matching current filters', tone: 'info' },
      { label: 'Valid Import Rows', value: this.currentImport?.batch?.validRows || 0, hint: `${rows.length} staged`, tone: 'success' },
      { label: 'Invalid Rows', value: this.currentImport?.batch?.invalidRows || 0, hint: 'Need correction', tone: 'warn' },
      { label: 'Processed', value: this.currentImport?.batch?.successRows || 0, hint: `${this.currentImport?.batch?.failedRows || 0} failed`, tone: 'default' }
    ];
  }

  loadCards(event?: TableLazyLoadEvent): void {
    if (event) {
      const rows = event.rows || this.pageSize;
      const first = event.first || 0;
      this.first = first;
      this.pageSize = rows;
      this.page = Math.floor(first / rows);
    }
    this.loading = true;
    this.service.list({
      owningCompanyId: this.currentCompanyId(),
      sourceCountryId: this.filterSourceCountryId,
      sourceCityId: this.filterSourceCityId,
      destinationCountryId: this.filterDestinationCountryId,
      destinationCityId: this.filterDestinationCityId,
      vehicleTypeId: this.filterVehicleTypeId,
      vehicleConfigId: this.filterVehicleConfigId,
      vehicleCapacityId: this.filterVehicleCapacityId,
      status: this.filterStatus,
      effectiveOn: this.formatDateOnly(this.filterEffectiveOn),
      keyword: this.keyword,
      page: this.page,
      pageSize: this.pageSize
    }).subscribe({
      next: response => {
        this.cards = response.items || [];
        this.totalRecords = response.count ?? this.cards.length;
        this.pageSize = Number(response.limit || this.pageSize);
        this.first = Number(response.offset || this.first);
        this.loading = false;
      },
      error: error => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Load failed', detail: error?.error?.message || error.message });
      }
    });
  }

  loadBatches(): void {
    this.service.batches(this.currentCompanyId()).subscribe({
      next: response => this.importBatches = response.items || [],
      error: () => this.importBatches = []
    });
  }

  applyFilters(): void {
    this.first = 0;
    this.page = 0;
    this.loadCards();
    this.loadBatches();
  }

  clearFilters(): void {
    this.filterSourceCountryId = undefined;
    this.filterSourceCityId = undefined;
    this.filterDestinationCountryId = undefined;
    this.filterDestinationCityId = undefined;
    this.filterVehicleTypeId = undefined;
    this.filterVehicleConfigId = undefined;
    this.filterVehicleCapacityId = undefined;
    this.filterStatus = undefined;
    this.filterEffectiveOn = null;
    this.keyword = '';
    this.applyFilters();
  }

  onFilterSourceCountryChange(): void {
    if (!this.cityBelongsToCountry(this.filterSourceCityId, this.filterSourceCountryId)) {
      this.filterSourceCityId = undefined;
    }
    this.ensureCitiesForCountry(this.filterSourceCountryId);
  }

  onFilterDestinationCountryChange(): void {
    if (!this.cityBelongsToCountry(this.filterDestinationCityId, this.filterDestinationCountryId)) {
      this.filterDestinationCityId = undefined;
    }
    this.ensureCitiesForCountry(this.filterDestinationCountryId);
  }

  onFilterVehicleTypeChange(): void {
    this.filterVehicleConfigId = undefined;
    this.filterVehicleCapacityId = undefined;
  }

  openCreate(): void {
    this.costForm = this.newCard();
    this.formVehicleConfigs = [];
    this.formVehicleCapacities = [];
    this.formEffectiveStartDate = null;
    this.formEffectiveEndDate = null;
    this.formVisible = true;
  }

  openEdit(card: InternalTransportCostCard): void {
    this.costForm = { ...card };
    this.formEffectiveStartDate = this.toDate(card.effectiveStartDate);
    this.formEffectiveEndDate = this.toDate(card.effectiveEndDate);
    this.updateFormVehicleOptions();
    this.ensureCitiesForCountry(card.sourceCountryId);
    this.ensureCitiesForCountry(card.destinationCountryId);
    this.formVisible = true;
  }

  saveCard(): void {
    const companyId = this.currentCompanyId();
    if (!companyId) {
      this.messageService.add({ severity: 'warn', summary: 'Company missing', detail: 'A company context is required.' });
      return;
    }
    const payload: InternalTransportCostCard = {
      ...this.costForm,
      owningCompanyId: companyId,
      effectiveStartDate: this.formatDateOnly(this.formEffectiveStartDate),
      effectiveEndDate: this.formatDateOnly(this.formEffectiveEndDate),
      status: this.costForm.status || 'ACTIVE'
    };
    this.saving = true;
    const request = payload.internalTransportCostCardId
      ? this.service.update(payload.internalTransportCostCardId, payload)
      : this.service.create(payload);
    request.subscribe({
      next: () => {
        this.saving = false;
        this.formVisible = false;
        this.loadCards();
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Internal transport cost card saved.' });
      },
      error: error => {
        this.saving = false;
        this.messageService.add({ severity: 'error', summary: 'Save failed', detail: error?.error?.message || error?.error || error.message });
      }
    });
  }

  onFormVehicleTypeChange(): void {
    this.costForm.vehicleConfigId = undefined;
    this.costForm.vehicleCapacityId = undefined;
    this.updateFormVehicleOptions();
  }

  onFormSourceCountryChange(): void {
    if (!this.cityBelongsToCountry(this.costForm.sourceCityId, this.costForm.sourceCountryId)) {
      this.costForm.sourceCityId = undefined;
    }
    this.ensureCitiesForCountry(this.costForm.sourceCountryId);
  }

  onFormDestinationCountryChange(): void {
    if (!this.cityBelongsToCountry(this.costForm.destinationCityId, this.costForm.destinationCountryId)) {
      this.costForm.destinationCityId = undefined;
    }
    this.ensureCitiesForCountry(this.costForm.destinationCountryId);
  }

  updateFormVehicleOptions(): void {
    this.formVehicleConfigs = this.vehicleConfigs.filter(config => config.typeId === this.costForm.vehicleTypeId);
    this.formVehicleCapacities = this.vehicleCapacities.filter(capacity => capacity.typeId === this.costForm.vehicleTypeId);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.importFile = input.files?.[0] || undefined;
  }

  uploadImport(): void {
    const companyId = this.currentCompanyId();
    if (!this.importFile || !companyId) {
      this.messageService.add({ severity: 'warn', summary: 'Missing data', detail: 'Select a file first.' });
      return;
    }
    this.loading = true;
    this.service.upload(this.importFile, companyId).subscribe({
      next: response => {
        this.currentImport = this.preparePreview(response.data);
        this.loading = false;
        this.loadBatches();
        this.messageService.add({ severity: 'success', summary: 'Uploaded', detail: 'Rows are ready for review.' });
      },
      error: error => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Upload failed', detail: error?.error?.message || error?.error || error.message });
      }
    });
  }

  downloadTemplate(format: 'xlsx' | 'csv'): void {
    this.loading = true;
    this.service.template(this.currentCompanyId(), format).subscribe({
      next: response => {
        this.loading = false;
        this.saveBlob(response.body!, `internal-transport-cost-template.${format}`);
      },
      error: error => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Template failed', detail: error?.error?.message || error?.error || error.message });
      }
    });
  }

  openBatch(batch: InternalTransportCostImportBatch): void {
    this.loading = true;
    this.service.batch(batch.batchId).subscribe({
      next: response => {
        this.currentImport = this.preparePreview(response.data);
        this.loading = false;
      },
      error: error => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Batch failed', detail: error?.error?.message || error?.error || error.message });
      }
    });
  }

  processBatch(): void {
    if (!this.currentImport?.batch?.batchId) {
      return;
    }
    this.processing = true;
    this.service.processBatch(this.currentImport.batch.batchId).subscribe({
      next: response => {
        this.currentImport = this.preparePreview({ batch: response.data.batch, rows: response.data.rows });
        this.processing = false;
        this.loadCards();
        this.loadBatches();
        this.messageService.add({ severity: 'success', summary: 'Processed', detail: 'Valid cost-card rows were processed.' });
      },
      error: error => {
        this.processing = false;
        this.messageService.add({ severity: 'error', summary: 'Process failed', detail: error?.error?.message || error?.error || error.message });
      }
    });
  }

  retryRow(row: InternalTransportCostImportRow): void {
    this.service.processRow(row.rowId).subscribe({
      next: response => {
        Object.assign(row, this.prepareRow(response.data));
        this.loadCards();
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Retry failed', detail: error?.error?.message || error?.error || error.message })
    });
  }

  openEditRow(row: InternalTransportCostImportRow): void {
    this.editingRow = this.prepareRow({ ...row, values: { ...(row.values || {}) } });
    this.editingValues = { ...(this.editingRow.values || {}) };
    this.editRowDialogVisible = true;
  }

  saveRow(): void {
    if (!this.editingRow) {
      return;
    }
    this.editingRow.values = this.editingValues;
    this.service.updateRow(this.editingRow).subscribe({
      next: response => {
        const index = this.currentImport?.rows.findIndex(row => row.rowId === response.data.rowId) ?? -1;
        if (this.currentImport && index >= 0) {
          this.currentImport.rows[index] = this.prepareRow(response.data);
        }
        this.editRowDialogVisible = false;
        this.refreshCurrentBatch();
        this.messageService.add({ severity: 'success', summary: 'Updated', detail: 'Row was revalidated.' });
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Update failed', detail: error?.error?.message || error?.error || error.message })
    });
  }

  rowImportValue(row: InternalTransportCostImportRow, key: string): string {
    const normalized = this.parsePayload(row.normalizedPayload);
    const raw = this.parsePayload(row.rawPayload);
    return this.firstText(normalized[key], row.values?.[key], raw[key]) || '-';
  }

  formatDateValue(value?: string | Date | null): string {
    if (!value) {
      return '-';
    }
    const date = this.toDate(value);
    return date ? date.toLocaleDateString('en-CA') : String(value);
  }

  currencyLabel(card: InternalTransportCostCard): string {
    return card.currencyCode || 'SAR';
  }

  statusSeverity(status?: InternalTransportCostImportStatus | string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
    if (status === 'SUCCESS' || status === 'COMPLETED' || status === 'ACTIVE') return 'success';
    if (status === 'VALID') return 'info';
    if (status === 'INVALID' || status === 'FAILED') return 'danger';
    if (status === 'PARTIAL_SUCCESS' || status === 'DRAFT' || status === 'PROCESSING') return 'warn';
    return 'secondary';
  }

  private newCard(): InternalTransportCostCard {
    return {
      currencyCode: 'SAR',
      status: 'ACTIVE'
    };
  }

  private currentCompanyId(): number | undefined {
    return this.session.getCompany()?.id || undefined;
  }

  private citiesForCountry(countryId?: number): City[] {
    if (!countryId) {
      return [];
    }
    return this.cityOptionsByCountry.get(countryId) ?? [];
  }

  private ensureCitiesForCountry(countryId?: number): void {
    if (!countryId || this.cityOptionsByCountry.has(countryId)) {
      return;
    }
    this.masterDataService.getCities('en-US', countryId).subscribe({
      next: cities => {
        const normalized = (cities ?? []).map(city => this.normalizeCity(city));
        this.cityOptionsByCountry.set(countryId, normalized);
        normalized.forEach(city => {
          if (city.id) {
            this.cityDirectory.set(city.id, city);
          }
        });
      },
      error: error => {
        this.messageService.add({ severity: 'error', summary: 'Cities failed', detail: error?.error?.message || error?.error || error.message });
      }
    });
  }

  private cityBelongsToCountry(cityId?: number, countryId?: number): boolean {
    if (!cityId || !countryId) {
      return false;
    }
    return this.cityCountryId(this.cityDirectory.get(cityId)) === countryId;
  }

  private normalizeCity(city: City): City {
    const raw = city as City & Record<string, unknown>;
    const normalized = {
      ...city,
      id: this.numberValue(city.id) ?? this.numberValue(raw['cityId']) ?? city.id,
      cityName: this.firstText(city.cityName, raw['displayName'], raw['name'], raw['city_name']) || city.cityName
    };
    if (!normalized.country) {
      const countryId = this.cityCountryId(normalized);
      normalized.country = this.countries.find(country => country.id === countryId) as Country;
    }
    return normalized;
  }

  private cityCountryId(city?: City): number | undefined {
    if (!city) {
      return undefined;
    }
    const raw = city as City & Record<string, unknown>;
    return this.numberValue(city.country?.id)
      ?? this.numberValue(raw['countryId'])
      ?? this.numberValue(raw['country_id']);
  }

  private preparePreview(preview: InternalTransportCostImportPreview): InternalTransportCostImportPreview {
    return {
      batch: preview.batch,
      rows: (preview.rows || []).map(row => this.prepareRow(row))
    };
  }

  private prepareRow(row: InternalTransportCostImportRow): InternalTransportCostImportRow {
    row.values = row.values || this.parsePayload(row.rawPayload || row.normalizedPayload);
    return row;
  }

  private refreshCurrentBatch(): void {
    if (this.currentImport?.batch?.batchId) {
      this.service.batch(this.currentImport.batch.batchId)
        .subscribe(response => this.currentImport = this.preparePreview(response.data));
    }
  }

  private parsePayload(payload?: string): Record<string, string> {
    if (!payload) return {};
    try {
      const parsed = JSON.parse(payload);
      return Object.keys(parsed || {}).reduce((acc, key) => {
        acc[key] = parsed[key] === null || parsed[key] === undefined ? '' : String(parsed[key]);
        return acc;
      }, {} as Record<string, string>);
    } catch {
      return {};
    }
  }

  private formatDateOnly(value?: Date | null): string | undefined {
    if (!value) {
      return undefined;
    }
    return value.toISOString().slice(0, 10);
  }

  private toDate(value?: string | Date | null): Date | null {
    if (!value) {
      return null;
    }
    return value instanceof Date ? value : new Date(`${value}T00:00:00`);
  }

  private firstText(...values: unknown[]): string {
    return values
      .map(value => value === null || value === undefined ? '' : String(value).trim())
      .find(value => !!value) ?? '';
  }

  private numberValue(value: unknown): number | undefined {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private saveBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    window.URL.revokeObjectURL(url);
  }
}
